import { HOTJAR_ID } from "@/constants/constants";
import { useUrlStorage } from "@/hooks/useUrlStorage";
import { configStore } from "@/stores/configStore";
import { deviceInfo } from "@/stores/deviceStore";
import { userStore } from "@/stores/userStore";
import { walletStore } from "@/stores/walletStore";

import { pointFormatterOrigin } from "../pointUtils";
import { getAppType, getOS, isAbandonedPlat } from "../utils";
import { OKSpinSDK } from "./OKSpin";
import { AdjustSDK } from "./adjust";
import { SDK } from "./base";
import { ChannelGTMSDK } from "./channelGTM";
import { FacebookSDK } from "./facebook";
import { GlobalGASDK } from "./globalGA";
import { GoogleAnalyticsSDK } from "./googleAnalytics";
import { GoogleTagManagerSDK } from "./googleTagManager";
import { HotjarSDK } from "./hotjar";
import { KwaiSDK } from "./kwai";
import { ReactNativeSDK } from "./reactNative";
import { SentrySDK } from "./sentry";
import { ThinkingDataSDK } from "./thinkingData";
import { TiktokSDK } from "./tiktok";
import { SDKName } from "./types";
import { WebViewSDK } from "./webView";

type SDKType =
  | typeof GoogleAnalyticsSDK
  | typeof HotjarSDK
  | typeof SentrySDK
  | typeof AdjustSDK
  | typeof FacebookSDK
  | typeof WebViewSDK
  | typeof ThinkingDataSDK
  | typeof ReactNativeSDK
  | typeof TiktokSDK
  | typeof GlobalGASDK
  | typeof GoogleTagManagerSDK
  | typeof ChannelGTMSDK
  | typeof OKSpinSDK;

type SDKInstances = {
  [K in SDKType["name"]]?: SDK;
};

const enum ReportPlatform {
  ReportPlat_GoogleAnalytics = 3, //google Analytics
  ReportPlat_Facebook = 4, //facebook
  ReportPlat_Adjust = 5, //adjust
  ReportPlat_KWAI = 8, //kwai
  ReportPlat_Tiktok = 11, //tiktok,
  ReportPlat_ChannelGTM = 7, //channelGTM,
  ReportPlat_OKSpin = 21, //googleTagManager,
}

export const enum googleType {
  sign_up = 1,
  sign_in = 2,
  retain_popup = 3,
}

interface PendingThinkingDataEvent {
  event: string;
  data: Record<string, unknown>;
}

class SDKManager {
  private static instance: SDKManager;
  private isWebView: boolean = false;
  private sdkInstances: SDKInstances = {};
  private _isInitBeforeLoad = false;
  private _isInitAfterLoad = false;
  private timeRecords: Record<string, { startTime: number; endTime?: number }> =
    {};
  private googleType: googleType = googleType.sign_up;
  private pendingThinkingDataEvents: PendingThinkingDataEvent[] = [];

  private constructor() {}

  public static getInstance(): SDKManager {
    if (!SDKManager.instance) {
      SDKManager.instance = new SDKManager();
    }
    return SDKManager.instance;
  }

  public registerSDK<T extends SDK>(sdk: T): void {
    this.sdkInstances[sdk.name] = sdk;
    // 如果注册的是 ThinkingData SDK，将临时队列中的事件转移到 SDK 的队列中
    if (sdk.name === SDKName.ThinkingDataSDK) {
      const thinkingData = this.getSDK<ThinkingDataSDK>(
        SDKName.ThinkingDataSDK,
      );
      if (thinkingData) {
        this.flushPendingThinkingDataEvents(thinkingData);
      }
    }
  }

  /** 将临时队列中的事件转移到 ThinkingData SDK 的队列中 */
  private flushPendingThinkingDataEvents(thinkingData: ThinkingDataSDK): void {
    while (this.pendingThinkingDataEvents.length > 0) {
      const pendingEvent = this.pendingThinkingDataEvents.shift();
      if (pendingEvent) {
        thinkingData.reportEvent(pendingEvent.event, pendingEvent.data);
      }
    }
  }

  public getSDK<T extends SDK>(name: string): T | undefined {
    return this.sdkInstances[name] as T | undefined;
  }

  public initBeforeLoad(): void {
    if (this._isInitBeforeLoad) return;
    this.isWebView = deviceInfo.webview;
    this.initializeBeforeLoadSDKs();
    this._isInitBeforeLoad = true;
  }

  public initAfterLoad(): void {
    if (this._isInitAfterLoad) return;
    this.initializeAfterLoadSDKs();
    this._isInitAfterLoad = true;
  }

  private initializeAfterLoadSDKs(): void {
    // 初始化所有支持的SDK
  }

  public async initialize(): Promise<void> {}

  public getReportSdkData() {
    if (!this.isWebView) {
      return;
    }
    return this.getSDK<WebViewSDK>(SDKName.WebViewSDK)?.getReportSdkData();
  }

  private initializeBeforeLoadSDKs(): void {
    this.initializeWebViewSDK();
    this.initializeBrowserSDKs();

    // 初始化所有注册的SDK
    Object.values(this.sdkInstances).forEach((sdk) => sdk?.initialize());
  }

  private initializeBrowserSDKs(): void {
    if (isAbandonedPlat()) {
      return;
    }
    // Google Analytics
    const gaId = configStore.configVersion?.report_configs?.find(
      (item) => item.platform === ReportPlatform.ReportPlat_GoogleAnalytics,
    )?.pixel_id;
    if (gaId) {
      this.registerSDK(
        new GoogleAnalyticsSDK({
          enabled: true,
          trackingId: gaId,
        }),
      );
    }

    const gtmId = configStore.frontCfgMap?.["ReportConfig"]?.gtmGlobal || "";
    if (gtmId) {
      this.registerSDK(
        new GoogleTagManagerSDK({
          enabled: true,
          trackingId: gtmId,
        }),
      );
    }
    const globalGaId =
      configStore.frontCfgMap?.["ReportConfig"]?.gaGlobal || "";
    if (globalGaId) {
      this.registerSDK(
        new GlobalGASDK({
          enabled: true,
          trackingId: globalGaId,
        }),
      );
    }
    // Hotjar
    const hotjarId = HOTJAR_ID;
    if (hotjarId) {
      this.registerSDK(
        new HotjarSDK({
          enabled: true,
          siteId: Number(hotjarId),
          sv: 6,
        }),
      );
    }

    // Sentry
    // this.registerSDK(
    //   new SentrySDK({
    //     enabled: false,
    //     dsn: import.meta.env.VITE_SENTRY_DSN,
    //     environment: this.environment,
    //   }),
    // );

    // Adjust
    const adjustToken = configStore.configVersion?.report_configs?.find(
      (item) => item.platform === ReportPlatform.ReportPlat_Adjust,
    )?.pixel_id;
    if (adjustToken) {
      this.registerSDK(
        new AdjustSDK({
          enabled: true,
          appToken: adjustToken,
        }),
      );
    }
    // Adjust
    // const channelGTMToken = configStore.configVersion?.report_configs?.find(
    //   (item) => item.platform === ReportPlatform.ReportPlat_ChannelGTM,
    // )?.pixel_id;
    // if (channelGTMToken) {
    //   this.registerSDK(
    //     new ChannelGTMSDK({
    //       enabled: true,
    //       trackingId: channelGTMToken,
    //     }),
    //   );
    // }
    const okSpinToken = configStore.configVersion?.report_configs?.find(
      (item) => item.platform === ReportPlatform.ReportPlat_OKSpin,
    )?.pixel_id;
    if (okSpinToken) {
      this.registerSDK(
        new OKSpinSDK({ enabled: true, trackingId: okSpinToken }),
      );
    }
    // Facebook
    const fbItem = configStore.configVersion?.report_configs?.find(
      (item) => item.platform === ReportPlatform.ReportPlat_Facebook,
    );
    if (fbItem?.pixel_id) {
      this.registerSDK(
        new FacebookSDK({
          enabled: true,
          pixelId: fbItem.pixel_id,
        }),
      );
    }

    // Kwai
    const kwaiPixelId = configStore.configVersion?.report_configs?.find(
      (item) => item.platform === ReportPlatform.ReportPlat_KWAI,
    )?.pixel_id;
    if (kwaiPixelId) {
      this.registerSDK(new KwaiSDK({ enabled: true, pixelId: kwaiPixelId }));
    }

    // ThinkingData
    const tdCfg =
      configStore.frontCfgMap?.["ReportConfig"]?.thinkingData || null;
    if (tdCfg && tdCfg.appId) {
      this.registerSDK(
        new ThinkingDataSDK({
          enabled: true,
          appId: tdCfg?.appId,
          showLog: !!tdCfg?.showLog,
          serverUrl: "https://us.thinkingdata.cc/",
          deviceId: deviceInfo.device_id,
        }),
      );
    }

    // Tiktok
    const tiktokPixelId = configStore.configVersion?.report_configs?.find(
      (item) => item.platform === ReportPlatform.ReportPlat_Tiktok,
    )?.pixel_id;
    if (tiktokPixelId) {
      this.registerSDK(
        new TiktokSDK({
          enabled: true,
          pixelId: tiktokPixelId,
        }),
      );
    }
  }

  private initializeWebViewSDK(): void {
    if (!this.isWebView) {
      return;
    }
    this.registerSDK(
      new WebViewSDK({
        enabled: true,
      }),
    );
  }

  public reportEvent(event: string, data: Record<string, unknown>) {
    let result: unknown = undefined;
    const results: unknown[] = [];
    const sdkNames = [
      SDKName.GoogleAnalyticsSDK,
      SDKName.GoogleTagManagerSDK,
      SDKName.GlobalGASDK,
      SDKName.ChannelGTMSDK,
    ];

    sdkNames.forEach((sdkName) => {
      const sdk = this.getSDK(sdkName);

      if (sdk) {
        const sdkResult = sdk.reportEvent(event, data);
        if (sdkResult !== undefined && sdkResult !== null) {
          results.push(sdkResult);
        }
      }
    });

    // 返回第一个有效结果，或者所有结果的数组
    result = results.length > 0 ? results[0] : undefined;
    return result;
  }

  public hideSplashScreen(): void {
    this.getSDK<WebViewSDK>(SDKName.WebViewSDK)?.hideSplashScreen?.();
  }

  public openAppBrowser(url: string): void {
    this.getSDK<WebViewSDK>(SDKName.WebViewSDK)?.openAppBrowser?.(url);
  }

  excucIosJsBridgeFunc(funcName: string, param: string) {
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.jsBridge &&
      window.webkit.messageHandlers.jsBridge.postMessage
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const funcParam: any = {};
      funcParam["funcName"] = funcName;
      funcParam["params"] = param;
      window.webkit.messageHandlers.jsBridge.postMessage(
        JSON.stringify(funcParam),
      );
    }
  }

  //此接口判断IOS马甲包
  checkIsIosJsBridgeApp() {
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.jsBridge
    ) {
      return true;
    }
    return false;
  }

  public tdReportEvent(event: string, data: Record<string, unknown>) {
    const thinkingData = this.getSDK<ThinkingDataSDK>(SDKName.ThinkingDataSDK);
    if (thinkingData) {
      // SDK 已注册，直接调用 reportEvent（如果未初始化会自动加入队列）
      thinkingData.reportEvent(`c_${event}`, data);
    } else {
      // SDK 还未注册，将事件存入临时队列
      this.pendingThinkingDataEvents.push({
        event: `c_${event}`,
        data,
      });
    }
  }
  public setStartTime(key: string, startTime: number) {
    this.timeRecords[key] = { startTime };
  }

  public okSpinReportEvent(event: string, data: Record<string, unknown>) {
    const okSpin = this.getSDK<OKSpinSDK>(SDKName.OKSpinSDK);
    if (okSpin) {
      okSpin.reportEvent(event, data);
    }
  }

  public setEndTime(key: string, endTime: number) {
    if (this.timeRecords[key]) {
      this.timeRecords[key].endTime = endTime;
    } else {
      this.timeRecords[key] = { startTime: 0, endTime };
    }
  }

  public setGoogleType(type: googleType) {
    this.googleType = type;
  }

  public getGoogleBtnEvent(): string {
    switch (this.googleType) {
      case googleType.sign_up:
        return "sign_up_page";
      case googleType.sign_in:
        return "sign_in_page";
      case googleType.retain_popup:
        return "cancel_signup_popup";
    }
  }

  public reportGoogleBtnEvent() {
    this.tdReportEvent(this.getGoogleBtnEvent(), {
      google_account_button_click: `${getAppType()}_${getOS()}`,
    });
  }

  public getTimeGap(key: string): number {
    const record = this.timeRecords[key];
    if (!record || !record.endTime) {
      return 0;
    }
    return record.endTime - record.startTime;
  }

  public getTimeRecord(
    key: string,
  ): { startTime: number; endTime?: number } | undefined {
    return this.timeRecords[key];
  }

  public clearTimeRecord(key: string): void {
    delete this.timeRecords[key];
  }

  public clearAllTimeRecords(): void {
    this.timeRecords = {};
  }

  public updateThinkingDataProperties(): void {
    const { getStoredParams } = useUrlStorage();
    const params = getStoredParams();
    const thinkingData = this.getSDK<ThinkingDataSDK>(SDKName.ThinkingDataSDK);
    if (thinkingData) {
      // 设置静态公共属性
      thinkingData.setSuperProperties({
        channel: userStore.isLogin
          ? userStore.userInfo?.pack_no
          : userStore.pack_no,
        promotion_dept: userStore.isLogin
          ? userStore.userInfo?.general
          : userStore.general,
        visitor_url: params?.url,
      });
      //动态公共事件属性【适合频繁变化的属性】，重新初始化 SDK 后，需要再次设置动态公共属性，且可以设置动态变量。
      // 范例：设置动态公共属性，在事件上报时触发回调函数，并把返回的JSON对象加入到事件属性中

      // 设置动态公共属性
      thinkingData.setDynamicSuperProperties(function () {
        const isLogin = userStore.isLogin;
        const bindPoint = walletStore.selectedWallet?.point;
        const bonus = walletStore.selectedWallet?.bonus;
        return {
          vip_level: userStore.userInfo?.UserVip?.vip_level,
          current_vip_level: userStore.userInfo?.UserVip?.vip_level,
          cash: isLogin ? pointFormatterOrigin(bindPoint || 0) : 0,
          bonus: isLogin ? pointFormatterOrigin(bonus || 0) : 0,
        };
      });
    }
  }
  public reportHjUserProperties(): void {
    const uid = userStore.userInfo?.uid || 0;
    const general = userStore.userInfo?.general || 0;
    const channel = userStore.userInfo?.pack_no || 0;
    if (window.hj && typeof window.hj === "function") {
      window.hj("identify", uid, { general: general, channel: channel });
    }
  }
}

export const sdkManager = SDKManager.getInstance();
